import type { Reminder } from '../stores/reminders'

// Groups one-time reminders by the local calendar day they fire on, soonest
// first, labelled the way the Reminders card reads them: "Today",
// "Tomorrow", "Yesterday", or a short date. Unparseable timestamps sort last
// under "No date" rather than disappearing.
export interface ReminderDayGroup {
  key: string
  label: string
  reminders: Reminder[]
}

const MS_PER_DAY = 86_400_000
const RELATIVE_LABELS: Record<number, string> = { [-1]: 'Yesterday', 0: 'Today', 1: 'Tomorrow' }
const NO_DATE_KEY = '9999-99-99'

function localDayKey(at: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
}

function dayOffset(at: Date, now: Date): number {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return Math.round((startOf(at) - startOf(now)) / MS_PER_DAY)
}

export function reminderDayLabel(at: Date, now: Date = new Date()): string {
  const relative = RELATIVE_LABELS[dayOffset(at, now)]
  if (relative) return relative
  const sameYear = at.getFullYear() === now.getFullYear()
  return at.toLocaleDateString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

export function reminderDayGroups(reminders: Reminder[], now: Date = new Date()): ReminderDayGroup[] {
  const groups = new Map<string, ReminderDayGroup>()
  const sorted = reminders.toSorted((a, b) => a.fire_at.localeCompare(b.fire_at))
  for (const reminder of sorted) {
    const at = new Date(reminder.fire_at)
    const valid = !Number.isNaN(at.getTime())
    const key = valid ? localDayKey(at) : NO_DATE_KEY
    let group = groups.get(key)
    if (!group) {
      group = { key, label: valid ? reminderDayLabel(at, now) : 'No date', reminders: [] }
      groups.set(key, group)
    }
    group.reminders.push(reminder)
  }
  return [...groups.values()].toSorted((a, b) => a.key.localeCompare(b.key))
}

// The clock time a reminder fires at, local and 24h-or-locale as the
// browser prefers; empty for an unparseable timestamp.
export function reminderClockTime(fireAt: string): string {
  const at = new Date(fireAt)
  if (Number.isNaN(at.getTime())) return ''
  return at.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}
